import { FC, useEffect, useState } from "react";
import { useAuth } from "../../../utility/useAuth";
import { getOperations, getOption, getRoutings } from "../../../services/ApiService";
import { DialogComponent } from "@syncfusion/ej2-react-popups";
import { Col, Container, Row } from "react-grid-system";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import { LabelText } from "./CompleteJobModal.styles";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import { CompleteJobDetails } from "../../CompleteJobDetails/CompleteJobDetails";
import { IUserInfo } from "../../../types/IUserInfo";
import { InspectionLog } from "../../../types/InspectionLog";
import { CompleteLeakTestJob } from "../../CompleteLeakTestJob/CompleteLeakTestJob";
import { IJob } from "../../../types/IJob";
import { IOperation } from "../../../types/IOperation";
import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import { CompleteProcedureType } from "../../../types/CompleteProcedureType";
import { CompletePressTestJob } from "../../CompletePressTestJob/CompletePressTestJob";
import { TestType } from "../../../types/TestType";
import { InspectionToolSet } from "../../../types/IInspectionToolSet";
import { CompletePressSetJob } from "../../CompletePressSetJob/CompletePressSetJob";

interface ICompleteJobModal {
  open: boolean;
  onCompleteJob: (
    jobNumber: string,
    operation: string,
    inspectionToolSet: InspectionToolSet,
    reason: string,
    operationStatus: string,
    inspectionLogs: InspectionLog[]
  ) => void;
  onClose: () => void;
  user: IUserInfo;
  job: IJob | undefined;
}

export const CompleteJobModal: FC<ICompleteJobModal> = ({
  open,
  onCompleteJob,
  onClose,
  user,
  job,
}) => {
  const dialogHeader = `Complete Operation for Job Number ${job?.jobNumber}`;

  const auth = useAuth();

  useEffect(() => {
    getOption(auth, "AllowCompleteOperationOutOfSequence")
      .then((val)=> {
        setAllowCompleteOutOfSequence(val === "True");
      })
      .catch((error) => {
        const message = `Failed to get AllowCompleteOperationOutOfSequence option. Error: ${error}`;
        console.error(message);
        ToastUtility.show(message, "Error", 2000);
      });
    getOperations(auth)
      .then((val) => {
        setOperations(val);
      })
      .catch((error) => {
        const message = `Failed to get operations. Error: ${error}`;
        console.error(`Failed to get operations. Error: ${error}`);
        ToastUtility.show(message, "Error", 2000);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (job?.jobNumber) {
      getRoutings(auth, job.jobNumber).then((routings) => {
        const jobOperation = routings
          .filter((x) => x.routingNumber >= job.routingNo)
          .map(({ operation }) => operation);
        setRoutings(jobOperation);
        setOperation(jobOperation[0]);
      });
    }
  }, [job, job?.jobNumber]);

  const [operation, setOperation] = useState("");
  const [completeProcedure, setCompleteProcedure] = useState("");
  const [operations, setOperations] = useState<IOperation[]>([]);
  const [routings, setRoutings] = useState<string[]>([]);
  const [operationStatus, setOperationStatus] = useState<string>();
  const [inspectionToolSet, setInspectionToolSet] = useState<InspectionToolSet>();
  const [reason, setReason] = useState<string>();
  const [inspectionLogs, setInspectionLogs] = useState<InspectionLog[]>();
  const [errors, setErrors] = useState("");
  const [allowCompleteOutOfSequence, setAllowCompleteOutOfSequence] = useState<boolean>();

  useEffect(() => {
    if (operations && operation) {
      setCompleteProcedure(
        operations.find((o) => o.resourceNo === operation)
          ?.completeProcedureType ?? "Standard"
      );
    }
  }, [operation, operations]);

  const buttons = [
    {
      buttonModel: {
        content: "Yes",
        cssClass: "e-flat",
        isPrimary: true,
        disabled: errors && errors.length > 0 ? true : false,
      },
      click: () => {
        if (
          inspectionToolSet &&
          inspectionLogs &&
          job?.jobNumber &&
          operationStatus
        ) {
          onCompleteJob(
            job.jobNumber,
            operation,
            inspectionToolSet,
            reason ?? "",
            operationStatus,
            inspectionLogs
          );
        }
      },
    },
    {
      buttonModel: {
        content: "No",
        cssClass: "e-flat",
        disabled: false,
      },
      click: () => {
        onClose();
      },
    },
  ];

  const firstPosition = { X: 200, Y: 50 };

  function onOperationChange(args: any) {
    if (args) {
      setOperation(args.value);
      setOperationStatus(undefined);
      setInspectionToolSet(undefined);
      setReason(undefined);
      setInspectionLogs([]);
    }
  }

  function onValidate(
    inspectionToolSet: InspectionToolSet | undefined,
    reason: string | undefined,
    operationStatus: string | undefined,
    inspectionLogs: InspectionLog[] | undefined
  ) {
    setInspectionToolSet(inspectionToolSet);
    setOperationStatus(operationStatus);
    setInspectionLogs(inspectionLogs);
    let err = "";
    if (!operationStatus || operationStatus.length === 0) {
      err += "The operation status is required. ";
    }
    if (!inspectionToolSet) {
      err += "The inspection tool is required. ";
    }
    if (inspectionLogs) {
      inspectionLogs.map((log) => {
        if (
          !log.isAccepted &&
          (!log.rejectionReason || log.rejectionReason.length === 0)
        ) {
          err += "The rejection reason is required. ";
        }
      });
    }
    setErrors(err);
  }

  return (
    <DialogComponent
      width="850px"
      close={onClose}
      header={dialogHeader}
      visible={open}
      showCloseIcon={true}
      buttons={buttons}
      position={firstPosition}
      isModal
    >
      <div className="dialogContent">
        <Container fluid>
          <Row align="stretch">
            <Col sm={4}>
              <LabelText>User</LabelText>
            </Col>
            <Col>
              <TextBoxComponent value={user.name} readOnly />
            </Col>
          </Row>
          <Row align="stretch">
            <Col sm={4}>
              <LabelText>Operation</LabelText>
            </Col>
            <Col>
              <DropDownListComponent
                id="ddlelement"
                enabled={allowCompleteOutOfSequence}
                dataSource={routings}
                value={operation}
                placeholder="Select an operation"
                onChange={onOperationChange}
              />
            </Col>
          </Row>
          {completeProcedure ===
          CompleteProcedureType[CompleteProcedureType.LeakTest] ? (
            <CompleteLeakTestJob
              job={job}
              operation={operation}
              user={user}
              onValidate={onValidate}
            />
          ) : (
            <></>
          )}
          {completeProcedure ===
          CompleteProcedureType[CompleteProcedureType.PressureTest] ? (
            <CompletePressTestJob
              job={job}
              operation={operation}
              testType={TestType.PressureTest}
              user={user}
              onValidate={onValidate}
            />
          ) : (
            <></>
          )}
          {completeProcedure ===
          CompleteProcedureType[CompleteProcedureType.PressureSet] ? (
            <CompletePressSetJob
              job={job}
              operation={operation}
              user={user}
              onValidate={onValidate}
            />
          ) : (
            <></>
          )}
          {!completeProcedure ||
          completeProcedure ===
            CompleteProcedureType[CompleteProcedureType.Standard] ? (
            <CompleteJobDetails
              jobNumber={job?.jobNumber ?? ""}
              operation={operation}
              qtyOrdered={job?.qtyOrdered ?? 0}
              userCode={user.code}
              onValidate={onValidate}
            />
          ) : (
            <></>
          )}
          <Row align="stretch">
            <h6 style={{ color: "red" }}>{errors}</h6>
          </Row>
        </Container>
      </div>
    </DialogComponent>
  );
};
